/**
 * Production Template Modal
 * 
 * Modal dialog for production template generation with:
 * - Multi-stage progress tracking (9 stages)
 * - Real-time SSE streaming updates
 * - Template preview on completion
 * - Error handling and retry
 * - Quality metrics display
 */

import React, { useEffect } from 'react';
import { Button, Modal, ProductionProgress } from '@tracekit/core';
import { getAPIBaseUrl, getAuthHeaders } from '@tracekit/core';
import { AlertCircle, Loader2, Maximize2, Minimize2 } from 'lucide-react';
import { useProductionTemplate } from '../../hooks/useProductionTemplate';
import { TemplatePreview } from './TemplatePreview';

interface ProductionTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  opportunityTitle?: string;
  projectId: string;
  artifactIds: string[];
  onComplete?: (result: any) => void; // Optional now - for backward compatibility
  onError?: (error: string) => void;
  onProposalJobSubmitted?: (jobId: string) => void; // New callback for direct job submission
}

export function ProductionTemplateModal({
  isOpen,
  onClose,
  opportunityId,
  opportunityTitle,
  projectId,
  artifactIds,
  onComplete,
  onError,
  onProposalJobSubmitted,
}: ProductionTemplateModalProps) {
  const {
    stage,
    message,
    progress,
    error,
    isStreaming,
    isLoading,
    elapsed,
    eta,
    result,
    metadata,
    jobId,
    startGeneration,
    cancelJobRemotelyAndStop,
    resumeJob,
    checkForExistingJob,
    loadCompletedResult,
    reset,
  } = useProductionTemplate();

  const [existingJob, setExistingJob] = React.useState<any>(null);
  const [checkingExisting, setCheckingExisting] = React.useState(false);
  const [cancelJobDialogOpen, setCancelJobDialogOpen] = React.useState(false);
  const [isCancellingJob, setIsCancellingJob] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(true);
  // Only auto-accept when we just finished generation in this session, not when we opened and loaded an already-completed job
  const didLoadPreExistingCompletedJob = React.useRef(false);

  // Stable key so effect does not re-run when parent passes a new artifactIds array reference with the same IDs
  const artifactIdsKey = React.useMemo(
    () => [...artifactIds].map(String).sort().join(','),
    [artifactIds]
  );

  // Latest stream/load state for open-init guard without putting isStreaming in effect deps (avoids re-entry / double POST)
  const streamGateRef = React.useRef({ isStreaming, isLoading, result, stage });
  streamGateRef.current = { isStreaming, isLoading, result, stage };

  // Helper to transform and accept result (defined early so it can be used in useEffect)
  const handleAcceptTemplateWithResult = React.useCallback((resultData: any) => {
    if (resultData) {
      // Transform result to pass criteria and questions (not RFP sections)
      // Map criteria fields to match ProposalBuilder expectations
      const transformedSections = (resultData.criteria || []).map((criterion: any) => ({
        // ProposalBuilder expects 'name' field
        name: criterion.title || criterion.name || `${criterion.id || criterion.criterion_id}`,
        // Keep title for compatibility
        title: criterion.title,
        // Map description
        description: criterion.description || '',
        // Keep all original criterion fields
        id: criterion.id || criterion.criterion_id,
        criterion_id: criterion.criterion_id || criterion.id,
        type: criterion.type || criterion.kind,
        kind: criterion.kind || criterion.type,
        weight: criterion.weight || criterion.points || 0,
        points: criterion.points || criterion.weight || 0,
        source_section: criterion.source_section,
        page: criterion.page,
        question_count: criterion.question_count || (criterion.questions?.length || 0),
        questions: criterion.questions || [],
        mapped_sections: criterion.mapped_sections || [],
        original_data: criterion.original_data,
      }));

      // Transform questions to match ProposalBuilder expectations
      const transformedQuestions = (resultData.questions || []).map((q: any) => ({
        // ProposalBuilder expects 'section' field (criterion name/id)
        section: q.criterion_id || q.section || transformedSections.find((s: any) => 
          s.id === q.criterion_id || s.criterion_id === q.criterion_id
        )?.name || 'Unknown',
        // ProposalBuilder expects 'question' field
        question: q.question || q.text || '',
        // Keep all original question fields
        id: q.id,
        criterion_id: q.criterion_id,
        text: q.text || q.question,
        type: q.type,
        metadata: q.metadata,
      }));

      const transformedResult = {
        ...resultData,
        // Use transformed criteria as sections (13 criteria, not 56 RFP sections)
        sections: transformedSections,
        // Use transformed questions (52 questions)
        questions: transformedQuestions,
        // Keep metadata
        metadata: resultData.metadata || {},
        // Keep other fields
        markdown: resultData.markdown,
        appendices: resultData.appendices,
        compliance_matrix: resultData.compliance_matrix,
        // Store original RFP sections separately for reference
        rfp_sections: resultData.sections || resultData.rfp_sections || [],
      };
      
      console.log('[ProductionTemplateModal] Passing to proposal builder:', {
        sections: transformedResult.sections?.length,
        sectionSample: transformedResult.sections?.[0],
        questions: transformedResult.questions?.length,
        appendices: transformedResult.appendices?.length,
      });
      
      // Call onComplete to update ProposalBuilder state and navigate
      // This is only called when user clicks "Accept & Use Template" button
      onComplete?.(transformedResult);
      
      // Close modal and reset after user accepts
      reset();
      onClose();
    }
  }, [onComplete, onClose, reset]);

  // Check for existing job once when modal opens.
  // Strict Mode: cleanup sets cancelled so only one branch runs startGeneration.
  // Deps use artifactIdsKey so a new array reference from the parent does not re-trigger init.
  useEffect(() => {
    if (!isOpen) {
      setCheckingExisting(false);
      return;
    }

    const gate = streamGateRef.current;
    if (gate.isStreaming || gate.isLoading || gate.result || gate.stage !== 'idle') {
      return;
    }

    let cancelled = false;

    (async () => {
      setCheckingExisting(true);
      try {
        const job = await checkForExistingJob(opportunityId, projectId);
        if (cancelled) return;

        if (job) {
          console.log('[ProductionTemplateModal] Found existing job:', job);

          if (job.status === 'completed' && job.result) {
            console.log('[ProductionTemplateModal] Job already completed, loading result directly...');
            didLoadPreExistingCompletedJob.current = true;
            loadCompletedResult(job.job_id, job.result, job.elapsed || 0);
          } else if (job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled') {
            console.log('[ProductionTemplateModal] Job in progress, showing resume banner...');
            setExistingJob(job);
          } else {
            console.log('[ProductionTemplateModal] Job in terminal state, starting new generation...', {
              status: job.status,
            });
            didLoadPreExistingCompletedJob.current = false;
            await startGeneration(opportunityId, projectId, artifactIds);
          }
        } else {
          console.log('[ProductionTemplateModal] No existing job, starting new generation...', {
            opportunityId,
            projectId,
            artifactIds,
          });
          didLoadPreExistingCompletedJob.current = false;
          await startGeneration(opportunityId, projectId, artifactIds);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[ProductionTemplateModal] Failed to check existing job:', err);
        didLoadPreExistingCompletedJob.current = false;
        await startGeneration(opportunityId, projectId, artifactIds);
      } finally {
        if (!cancelled) {
          setCheckingExisting(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, opportunityId, projectId, artifactIdsKey, startGeneration, checkForExistingJob, loadCompletedResult]);

  // Handle resume existing job
  const handleResumeExistingJob = () => {
    if (existingJob) {
      console.log('[ProductionTemplateModal] Resuming existing job:', existingJob);
      resumeJob(opportunityId, projectId, existingJob.job_id, existingJob.progress, existingJob.stage);
      setExistingJob(null);
    }
  };

  // Handle start new job (cancel existing)
  const handleStartNewJob = () => {
    console.log('[ProductionTemplateModal] Starting new job (cancelling existing)...');
    setExistingJob(null);
    didLoadPreExistingCompletedJob.current = false;
    startGeneration(opportunityId, projectId, artifactIds);
  };

  // When template just completed in this session: auto-navigate (don't when we opened and loaded an already-completed job)
  useEffect(() => {
    if (stage === 'complete' && result && !didLoadPreExistingCompletedJob.current) {
      console.log('[ProductionTemplateModal] Generation complete — auto-accepting and navigating.');
      const t = setTimeout(() => {
        handleAcceptTemplateWithResult(result);
      }, 600);
      return () => clearTimeout(t);
    }
  }, [stage, result, handleAcceptTemplateWithResult]);

  // Handle errors
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Handle close modal (X button - just closes, job continues)
  const handleCloseModal = () => {
    if (isStreaming) {
      const confirmClose = window.confirm(
        'Template generation is in progress. The job will continue in the background and you can resume it later. Close modal?'
      );
      if (!confirmClose) return;
    }
    // Just close modal - job continues in background
    onClose();
  };

  // First step: user clicked "Cancel Job" — show in-modal confirmation (reliable vs window.confirm / missing jobId)
  const handleCancelJobClick = () => {
    setCancelJobDialogOpen(true);
  };

  const handleConfirmCancelJob = async () => {
    try {
      setIsCancellingJob(true);
      await cancelJobRemotelyAndStop(opportunityId, projectId);
      setCancelJobDialogOpen(false);
      reset();
      onClose();
    } catch (e) {
      console.error('[ProductionTemplateModal] Cancel job failed:', e);
      setCancelJobDialogOpen(false);
      reset();
      onClose();
    } finally {
      setIsCancellingJob(false);
    }
  };

  React.useEffect(() => {
    if (!isOpen) {
      setCancelJobDialogOpen(false);
      setIsCancellingJob(false);
    }
  }, [isOpen]);

  // Open this modal in fullscreen by default each time.
  React.useEffect(() => {
    if (isOpen) setIsFullscreen(true);
  }, [isOpen]);

  // Handle retry
  const handleRetry = () => {
    reset();
    startGeneration(opportunityId, projectId, artifactIds);
  };

  // Handle accept template (manual button click)
  const handleAcceptTemplate = () => {
    if (result) {
      // Fetch full job result from API first to ensure we have complete data
      const fetchAndAccept = async () => {
        try {
          if (jobId) {
            const baseUrl = getAPIBaseUrl();
            const authHeaders = getAuthHeaders();
            const statusUrl = `${baseUrl}/api/opportunities/${opportunityId}/production-template/status/${jobId}?project_id=${encodeURIComponent(projectId)}`;
            
            const response = await fetch(statusUrl, {
              method: 'GET',
              headers: {
                ...authHeaders,
              },
            });
            
            if (response.ok) {
              const jobStatus = await response.json();
              const finalResult = jobStatus.result || result;
              handleAcceptTemplateWithResult(finalResult);
            } else {
              handleAcceptTemplateWithResult(result);
            }
          } else {
            handleAcceptTemplateWithResult(result);
          }
        } catch (error) {
          console.error('[ProductionTemplateModal] Error fetching job result:', error);
          handleAcceptTemplateWithResult(result);
        }
      };
      
      fetchAndAccept();
    }
  };

  // Handle regenerate template
  const handleRegenerateTemplate = () => {
    reset();
    startGeneration(opportunityId, projectId, artifactIds);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title="Production Template Generation"
      size="full"
      contentFrameClassName={
        isFullscreen
          ? 'flex min-h-full items-stretch justify-center p-2 sm:p-3'
          : undefined
      }
      panelClassName={
        isFullscreen
          ? '!max-w-[100vw] flex max-h-[100dvh] min-h-0 flex-col overflow-hidden'
          : undefined
      }
      bodyClassName={
        isFullscreen ? 'flex min-h-0 flex-1 flex-col overflow-y-auto' : undefined
      }
    >
      <div className="space-y-4">
        {/* Match Generate Proposal: fullscreen toggle in body, single close in modal chrome */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setIsFullscreen((v) => !v)}
            className="p-1 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>

        {/* Header with artifact info */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-theme-text">
              Processing RFP Documents
            </h3>
            <p className="text-xs text-gray-600 dark:text-theme-text-secondary mt-1">
              {artifactIds.length} {artifactIds.length === 1 ? 'document' : 'documents'} selected
            </p>
          </div>
        </div>

        {/* Checking for Existing Job */}
        {checkingExisting && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin" />
            <p className="text-gray-600 dark:text-theme-text-secondary">
              Checking for in-progress jobs...
            </p>
          </div>
        )}

        {/* Existing Job Found - Resume or Start New */}
        {existingJob && existingJob.status !== 'completed' && !isStreaming && (
          <div className="py-8">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-2">
                    In-Progress Job Found
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-4">
                    A template generation job is already running for this opportunity. You can resume it or start a new one.
                  </p>
                  
                  {/* Progress Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Progress: </span>
                        <span className="font-medium text-blue-700 dark:text-blue-300">{existingJob.progress || 0}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Stage: </span>
                        <span className="font-medium text-gray-900 dark:text-theme-text capitalize">
                          {(existingJob.stage || 'queued').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Elapsed Time */}
                    {existingJob.elapsed !== undefined && existingJob.elapsed > 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Running for: {Math.floor(existingJob.elapsed / 60)}m {Math.round(existingJob.elapsed % 60)}s
                        {existingJob.eta && existingJob.eta > 0 && (
                          <> • ETA: {Math.floor(existingJob.eta / 60)}m {Math.round(existingJob.eta % 60)}s</>
                        )}
                      </div>
                    )}
                    
                    {/* Current Message */}
                    {existingJob.message && (
                      <div className="text-xs text-gray-600 dark:text-gray-400 italic">
                        {existingJob.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="primary" onClick={handleResumeExistingJob}>
                  Resume Job
                </Button>
                <Button variant="outline" onClick={handleStartNewJob}>
                  Start New Job
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && !checkingExisting && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin" />
            <p className="text-gray-600 dark:text-theme-text-secondary">
              Starting production pipeline...
            </p>
          </div>
        )}

        {/* Progress State */}
        {isStreaming && !error && (
          <ProductionProgress
            stage={stage}
            progress={progress}
            message={message}
            elapsed={elapsed}
            eta={eta}
            metadata={metadata}
          />
        )}

        {/* Error State */}
        {error && !isStreaming && (
          <div className="py-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-center max-w-md">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-2">
                  Generation Failed
                </h3>
                <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-4">
                  {error}
                </p>
                <div className="text-xs text-gray-500 dark:text-theme-text-secondary mb-4">
                  <p>Elapsed time: {Math.floor(elapsed / 60)}m {Math.round(elapsed % 60)}s</p>
                  {stage !== 'idle' && <p>Failed at stage: {stage}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Complete but result still loading (e.g. fetching after event: progress stage=complete) */}
        {stage === 'complete' && !result && !error && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin" />
            <p className="text-gray-600 dark:text-theme-text-secondary">
              Preparing template...
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Fetching generated template. Please wait.
            </p>
          </div>
        )}

        {/* Success State - Template Preview */}
        {stage === 'complete' && result && (
          <TemplatePreview
            result={result}
            opportunityId={opportunityId}
            opportunityTitle={opportunityTitle}
            onAccept={handleAcceptTemplate}
            onClose={handleCloseModal}
            onRegenerate={handleRegenerateTemplate}
            onProposalSubmitted={onProposalJobSubmitted}
          />
        )}

        {/* Cancel job confirmation (in-modal; avoids silent onClose when jobId was missing from state) */}
        {cancelJobDialogOpen && isStreaming && (
          <div
            role="alertdialog"
            aria-labelledby="cancel-production-job-title"
            aria-describedby="cancel-production-job-desc"
            className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-4 space-y-3"
          >
            <h4
              id="cancel-production-job-title"
              className="text-sm font-semibold text-gray-900 dark:text-theme-text"
            >
              Cancel template generation?
            </h4>
            <p
              id="cancel-production-job-desc"
              className="text-sm text-gray-700 dark:text-theme-text-secondary"
            >
              This will request cancellation on the server (the worker should stop shortly) and close this
              window. You can start a new generation later if needed.
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                type="button"
                disabled={isCancellingJob}
                onClick={() => setCancelJobDialogOpen(false)}
              >
                Keep running
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={isCancellingJob}
                onClick={handleConfirmCancelJob}
              >
                {isCancellingJob ? 'Cancelling…' : 'Yes, cancel job'}
              </Button>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-theme-text-secondary">
            {elapsed > 0 && (
              <span>
                Elapsed: {Math.floor(elapsed / 60)}m {Math.round(elapsed % 60)}s
                {eta && eta > 0 && (
                  <> • ETA: {Math.floor(eta / 60)}m {Math.round(eta % 60)}s</>
                )}
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            {/* Cancel Job — opens in-modal confirmation */}
            {isStreaming && !cancelJobDialogOpen && (
              <Button variant="outline" onClick={handleCancelJobClick}>
                Cancel Job
              </Button>
            )}
            
            {/* Retry button (shown on error) */}
            {error && !isStreaming && (
              <Button variant="primary" onClick={handleRetry}>
                Retry
              </Button>
            )}
            
            {/* Close button (shown when not streaming and no result) */}
            {!isStreaming && !result && !isLoading && (
              <Button variant="outline" onClick={handleCloseModal}>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
